import {useQuery, UseQueryResult} from '@tanstack/react-query';
import {camelizeKeys} from 'humps';
import {z} from 'zod';

import {ReviewsStateType} from '../../page/main/reviews/reviews-state/reviews-state-type';
import {useUser} from '../../provider/user/user-hook';
import {createRaceFetchX} from '../../util/fetch';
import {objectToUrlParameters} from '../../util/url';
import {rootApiUrl} from '../api/api-const';

import {reviewsStateFilterToUrlObject} from './reviews-helper';

const ratingListSchema = z.object({
    0: z.number(),
    1: z.number(),
    2: z.number(),
    3: z.number(),
    4: z.number(),
    5: z.number(),
});

type RatingListKeyType = keyof z.infer<typeof ratingListSchema>;

const ratingListKeysSet = new Set(Object.keys(ratingListSchema.shape).map((key) => Number.parseInt(key, 10)));

export function isRatingListKey(keyQualifier: unknown): keyQualifier is RatingListKeyType {
    return typeof keyQualifier === 'number' && ratingListKeysSet.has(keyQualifier);
}

const reviewsStatisticSchema = z.object({
    count: z.number(),
    negative: z.number(),
    neutral: z.number(),
    positive: z.number(),
    ratingAvg: z.number(),
    ratingList: ratingListSchema,
});

export type ReviewsStatisticDataType = z.infer<typeof reviewsStatisticSchema>;

const reviewsStatisticRaceFetchX = createRaceFetchX();

function getReviewsStatisticUrl(reviewOption: ReviewsStateType) {
    const searchParameters = objectToUrlParameters({
        ...(!reviewOption.company_ids ? {filter_key: reviewOption.filterId} : null),
        ...reviewsStateFilterToUrlObject(reviewOption),
    });

    return `${rootApiUrl}/v4/reviews/statistic/?${searchParameters}`;
}

function fetchReviewsStatistic(reviewOption: ReviewsStateType): Promise<ReviewsStatisticDataType> {
    return reviewsStatisticRaceFetchX<unknown>(getReviewsStatisticUrl(reviewOption)).then((payload) =>
        reviewsStatisticSchema.parse(camelizeKeys(payload))
    );
}

export function useReviewsStatistic(reviewsState: ReviewsStateType): UseQueryResult<ReviewsStatisticDataType> {
    const {user} = useUser();

    return useQuery(
        [getReviewsStatisticUrl(reviewsState), user?.profile?.isShowAutotags],
        () => fetchReviewsStatistic(reviewsState),
        {
            refetchOnMount: false,
            keepPreviousData: true,
            refetchOnWindowFocus: false,
        }
    );
}
